import type {
    HistoryAnnotationModelOption,
    HistoryPrediction,
} from '../types/history'

export interface AnnotationPredictionRemapResult {
    predictions: HistoryPrediction[]
    removedIdentityKeys: string[]
}

const normalizeRawClass = (value?: string | null): string =>
    typeof value === 'string' ? value.trim().toLowerCase() : ''

const getPredictionIdentityKey = (prediction: HistoryPrediction): string => {
    const modelKey = normalizeRawClass(prediction.model_key) || 'mosquito'
    const rawClass = normalizeRawClass(
        prediction.raw_class_norm ||
            prediction.raw_class ||
            prediction.eng_class ||
            prediction.class,
    )
    return `${modelKey}:${rawClass}`
}

export const remapPredictionsForAnnotationModel = (
    predictions: HistoryPrediction[],
    targetModel: HistoryAnnotationModelOption,
): AnnotationPredictionRemapResult => {
    const labelOptionsByIdentity = new Map(
        targetModel.label_options.map((option) => [
            option.identity_key ||
                `${normalizeRawClass(option.model_key)}:${normalizeRawClass(
                    option.raw_class_norm ||
                        option.raw_class ||
                        option.eng_class,
                )}`,
            option,
        ]),
    )
    const remapped: HistoryPrediction[] = []
    const removedIdentityKeys: string[] = []

    predictions.forEach((prediction) => {
        const identityKey = getPredictionIdentityKey(prediction)
        const option = labelOptionsByIdentity.get(identityKey)
        if (!option) {
            removedIdentityKeys.push(identityKey)
            return
        }
        remapped.push({
            ...prediction,
            model_key: option.model_key || targetModel.model_key,
            raw_class: option.raw_class || option.eng_class,
            raw_class_norm:
                option.raw_class_norm || normalizeRawClass(option.eng_class),
            eng_class: option.eng_class,
            class: option.class,
            cls_id: option.cls_id,
        })
    })

    return { predictions: remapped, removedIdentityKeys }
}

export const remapPredictionsForAnnotationModels = (
    predictions: HistoryPrediction[],
    targetModels: HistoryAnnotationModelOption[],
): AnnotationPredictionRemapResult => {
    const labelOptionsByIdentity = new Map(
        targetModels.flatMap((targetModel) =>
            targetModel.label_options.map(
                (option) =>
                    [
                        option.identity_key ||
                            `${normalizeRawClass(option.model_key)}:${normalizeRawClass(
                                option.raw_class_norm ||
                                    option.raw_class ||
                                    option.eng_class,
                            )}`,
                        { option, targetModel },
                    ] as const,
            ),
        ),
    )
    const remapped: HistoryPrediction[] = []
    const removedIdentityKeys: string[] = []

    predictions.forEach((prediction) => {
        const identityKey = getPredictionIdentityKey(prediction)
        const match = labelOptionsByIdentity.get(identityKey)
        if (!match) {
            removedIdentityKeys.push(identityKey)
            return
        }
        const { option, targetModel } = match
        remapped.push({
            ...prediction,
            model_key: option.model_key || targetModel.model_key,
            raw_class: option.raw_class || option.eng_class,
            raw_class_norm:
                option.raw_class_norm || normalizeRawClass(option.eng_class),
            eng_class: option.eng_class,
            class: option.class,
            cls_id: option.cls_id,
        })
    })

    return { predictions: remapped, removedIdentityKeys }
}
